import Vue from 'vue'
import $api from '@/shared/services/api'
import { chatIa } from '@/shared/config/chatIa'

/*
 * Lancador do chat do ClickDesk (assistente de IA).
 *
 * O frontend nao conhece marca, chave nem segredo: pede a identidade ao
 * backend (/usuarios/chat_identidade), que resolve modalidade -> marca ->
 * widget_key + agente e devolve junto o auth/sig e o contexto (meta).
 *
 * Config (URL do widget, kill-switch, provider) vem de shared/config/chatIa.
 */

const raiz = () => chatIa
const cfg = () => chatIa.desk

// A URL do widget vem do backend junto da chave (a config local e fallback).
const URL_WIDGET = (dados) => (dados && dados.widget_url) || cfg().widgetUrl || ''

// E o endereco da API TEM de ser informado: o bundle do widget nao deduz nada
// da origem do script — ele traz `https://api.desk.click.app/api/v1` assado
// dentro, entao sem isto todo ambiente que nao e producao conversa com a API
// de PRODUCAO. Vem do backend (api_url da identidade, por modalidade); a
// config local (VUE_APP_CHAT_DESK_API_URL) e o fallback.
const URL_API = (dados) => (dados && dados.api_url) || cfg().apiUrl || ''

// Provider ativo. Hoje e sempre o Desk (ver shared/config/chatIa); a checagem
// segue existindo enquanto o assistente antigo nao for removido (fase 5).
export const usaChatDesk = () => chatIa.provider === 'desk'

let identidade = null
let montado = false

/* Baixa o bundle do widget uma unica vez. */
const carregaScript = (src) => new Promise((resolve, reject) => {
    // A URL entra na mensagem: quase todo erro aqui e endereco de outro
    // ambiente (o widget do dev apontado em homologacao, por exemplo) ou
    // bloqueio de conteudo misto — http servido dentro de uma pagina https.
    const erro = () => new Error(`Falha ao carregar o assistente (${src}).`)
    if (window.Chat) {
        return resolve()
    }
    const existente = document.querySelector(`script[src="${src}"]`)
    if (existente) {
        existente.addEventListener('load', () => resolve())
        existente.addEventListener('error', () => reject(erro()))
        return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(erro())
    document.head.appendChild(script)
})

/* Busca identidade assinada no backend. */
const buscaIdentidade = async () => {
    // Endereco absoluto do plugin AssistenteIa, o mesmo em qualquer modulo:
    // antes era $urlBase + '/usuarios/chat_identidade', que dependia do
    // rotasPadroes de cada modulo e de uma acao no UsuariosController do core.
    const retorno = await $api.cGet('/assistente_ia/agente/identidade', {}, false)
    if (retorno.status != 'success' || !retorno.data || !retorno.data.key) {
        return null
    }
    return retorno.data
}

/*
 * Informa quem esta navegando. O identify vale na PROXIMA abertura do chat,
 * entao ele sempre roda antes do open.
 */
const identifica = () => {
    if (!identidade || !window.Chat) {
        return
    }
    window.Chat.identify({
        name: Vue.prototype.$usuarioLogado ? Vue.prototype.$usuarioLogado.nome : '',
        email: Vue.prototype.$usuarioLogado ? Vue.prototype.$usuarioLogado.email : '',
        meta: identidade.meta,
        auth: identidade.auth,
        sig: identidade.sig,
    })
}

/*
 * Abre o assistente. Usa o icone da barra superior como lancador, entao a
 * bolha flutuante do widget fica escondida (e nao briga com a do Zendesk).
 */
export const abreChatDesk = async ({ launcherSelector = '.nav-link-ia', container = null, aoFechar = null, aoExpandir = null } = {}) => {
    if (raiz().enabled === false) {
        Vue.prototype.$dialog.error(
            raiz().disabledMessage || 'O assistente está temporariamente indisponível. Tente novamente em instantes.'
        )
        return false
    }

    identidade = await buscaIdentidade()
    if (!identidade) {
        Vue.prototype.$dialog.error('Não foi possível iniciar o assistente. Tente novamente em instantes.')
        return false
    }

    const urlWidget = URL_WIDGET(identidade)
    if (!urlWidget) {
        Vue.prototype.$dialog.error(
            raiz().disabledMessage || 'O assistente está temporariamente indisponível. Tente novamente em instantes.'
        )
        return false
    }

    await carregaScript(urlWidget)
    if (!window.Chat) {
        Vue.prototype.$dialog.error('Não foi possível carregar o assistente. Tente novamente em instantes.')
        return false
    }

    if (!montado) {
        // Formato e definido no init e nao muda depois: para trocar entre
        // embutido e bolha e preciso destruir e montar de novo.
        const config = {
            key: identidade.key,
            // Para qual Desk o widget fala. Ver URL_API acima: omitir isto nao
            // e "usar o padrao do ambiente", e sim cair na producao.
            api: URL_API(identidade),
            // A identidade vai JA no init: o identify so passa a valer na
            // proxima abertura, e no modo embutido a conversa comeca junto com
            // a montagem — sem isso a primeira conversa nasce anonima.
            name: Vue.prototype.$usuarioLogado ? Vue.prototype.$usuarioLogado.nome : '',
            email: Vue.prototype.$usuarioLogado ? Vue.prototype.$usuarioLogado.email : '',
            meta: identidade.meta,
            auth: identidade.auth,
            sig: identidade.sig,
        }
        // Avatares resolvidos no backend: o do agente e o favicon do dominio
        // ao qual a empresa esta vinculada; o do usuario e a foto dele.
        if (identidade.agent_avatar) {
            config.agentAvatar = identidade.agent_avatar
        }
        if (identidade.user_avatar) {
            config.userAvatar = identidade.user_avatar
        }
        if (container) {
            // Dentro da pagina: ocupa 100% do elemento indicado. O topo neutro
            // evita a faixa colorida da marca no meio da nossa interface — a
            // cor dela continua valendo em baloes, botoes e destaques.
            //
            // `logo` e o do TOPO e nao se confunde com o `agentAvatar` das
            // respostas: sao imagens independentes. Sem ele, o topo cairia no
            // agentAvatar e as duas ficariam iguais.
            config.container = container
            // No modo embutido o widget esconde o botao de expandir por
            // padrao: ele ocupa 100% do container, entao expandir sozinho nao
            // faria nada. Com canExpand ele mostra o botao e emite o evento
            // 'expand' — quem alarga de verdade e a nossa sidebar, no evento.
            config.canExpand = true
            config.header = {
                style: 'plain',
                logo: cfg().headerLogo,
                title: cfg().title || 'Assistente de IA',
                subtitle: cfg().subtitle !== undefined ? cfg().subtitle : '',
            }
        } else {
            // Bolha escondida: quem abre e o nosso icone da barra superior.
            config.hideLauncher = true
            config.launcherSelector = launcherSelector
        }
        window.Chat.init(config)
        montado = true
        // O widget embutido nao sabe da nossa sidebar: o X dele fecha o chat,
        // e o evento e que tira a sidebar da tela junto.
        if (aoFechar && typeof window.Chat.on === 'function') {
            window.Chat.on('close', aoFechar)
        }
        // Expandir do widget embutido: o clique so alterna o modo largo
        // interno e avisa com true/false — alargar o painel e conosco.
        if (aoExpandir && typeof window.Chat.on === 'function') {
            window.Chat.on('expand', aoExpandir)
        }
    }

    // Identidade tambem pelo identify, e o open logo em seguida: o identify so
    // vale "na proxima abertura", entao sem o open o modo embutido ficaria
    // esperando uma abertura que nunca acontece — e a conversa nasce anonima.
    identifica()
    window.Chat.open()
    return true
}

/*
 * Renova a identidade e reavisa o widget. Chamar apos o login e sempre que o
 * token de chat expirar — o painel aberto na hora da troca precisa ser
 * remontado, senao continua com a identidade anterior.
 */
export const renovaChatDesk = async () => {
    identidade = await buscaIdentidade()
    if (!identidade || !window.Chat) {
        return false
    }
    if (montado) {
        window.Chat.destroy()
        montado = false
    }
    return true
}

/* Encerra a conversa atual, mantendo o widget montado. */
export const encerraChatDesk = () => {
    if (window.Chat && montado) {
        window.Chat.end()
    }
}

/* Logout: tira o chat da tela para nao deixar a identidade para o proximo. */
export const removeChatDesk = () => {
    identidade = null
    if (window.Chat && montado) {
        window.Chat.destroy()
    }
    montado = false
}
